import { DateTime } from 'luxon'

export const formatSlots = (slots) => {
  return slots.reduce(
    (obj, slot) => ({
      ...obj,
      [slot.day]: [
        ...(obj[slot.day] ? obj[slot.day] : []),
        {
          startTime: slot.startTime,
          endTime: slot.endTime
        }
      ]
    }),
    {}
  )
}

// Describes the payment deadline the API returns on a new booking (#48, ADR
// 0012) for a sentence in the booker's language. null when there is none:
// the cleanup is off or the booking is paid with a strippenkaart.
export const describePaymentDeadline = (
  iso,
  now = new Date(),
  locale = 'nl'
) => {
  if (!iso) return null
  const zone = 'Europe/Amsterdam'
  const at = DateTime.fromISO(iso, { zone }).setLocale(locale)
  if (!at.isValid) return null
  const today = DateTime.fromJSDate(now, { zone })
  const when = at.hasSame(today, 'day')
    ? 'today'
    : at.hasSame(today.plus({ days: 1 }), 'day')
    ? 'tomorrow'
    : 'later'
  return { when, time: at.toFormat('HH:mm'), date: at.toFormat('cccc d LLLL') }
}

// Location page (#280): the date cards cover the booking window of the
// location, from today up to and including the day of now +
// max_hours_before_booking (the window the backend enforces, and the last
// day the old date picker offered). 0 (or unset) means "no limit"; the
// cards then show a fixed number of days.
export const DEFAULT_BOOKING_WINDOW_DAYS = 30

export const bookingWindowDays = (
  maxHoursBeforeBooking,
  now = DateTime.local()
) => {
  if (!(maxHoursBeforeBooking > 0)) return DEFAULT_BOOKING_WINDOW_DAYS
  const today = now.startOf('day')
  const end = now.plus({ hours: maxHoursBeforeBooking })
  const lastDay = end.startOf('day')
  // a window that ends exactly at midnight has nothing left on that day
  const days =
    Math.round(lastDay.diff(today, 'days').days) + (+end === +lastDay ? 0 : 1)
  return Math.max(1, days)
}

const WEEKDAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za']
const WEEKDAYS_FULL = [
  'zondag',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag'
]
const MONTHS = [
  'jan',
  'feb',
  'mrt',
  'apr',
  'mei',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec'
]
const MONTHS_FULL = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december'
]

// One card per day from today: weekday + day number, the month only on the
// first card and on the 1st of a month. `key` is the ISO date (yyyy-MM-dd).
export const buildDateCards = (count, today = DateTime.local()) => {
  const start = today.startOf('day')
  return Array.from({ length: count }, (_, i) => {
    const d = start.plus({ days: i })
    const weekday = d.weekday % 7
    return {
      key: d.toISODate(),
      weekday: WEEKDAYS[weekday],
      day: d.day,
      month: i === 0 || d.day === 1 ? MONTHS[d.month - 1] : '',
      ariaLabel: `${i === 0 ? 'Vandaag, ' : ''}${WEEKDAYS_FULL[weekday]} ${
        d.day
      } ${MONTHS_FULL[d.month - 1]}`
    }
  })
}

// "za 24 okt" (short) or "zaterdag 24 okt" (long) for an ISO date.
export const formatBookingDate = (isoDate, long = false) => {
  const d = DateTime.fromISO(isoDate)
  if (!d.isValid) return ''
  const weekday = d.weekday % 7
  return `${long ? WEEKDAYS_FULL[weekday] : WEEKDAYS[weekday]} ${d.day} ${
    MONTHS[d.month - 1]
  }`
}

// "oktober 2026" for an ISO date.
export const formatBookingMonth = (isoDate) => {
  const d = DateTime.fromISO(isoDate)
  return d.isValid ? `${MONTHS_FULL[d.month - 1]} ${d.year}` : ''
}

// "19:00–20:30" for a start time (HH:mm) and a duration in minutes.
export const formatTimeRange = (time, duration, separator = '–') => {
  const start = DateTime.fromFormat(time || '', 'HH:mm')
  if (!start.isValid) return ''
  return duration
    ? `${time}${separator}${start
        .plus({ minutes: duration })
        .toFormat('HH:mm')}`
    : time
}

// "45 minuten", "1 uur", "anderhalf uur", "2 uur 15 min"
export const durationLabel = (duration) => {
  if (duration < 60) return `${duration} minuten`
  if (duration === 90) return 'anderhalf uur'
  const hours = Math.floor(duration / 60)
  const minutes = duration % 60
  return minutes ? `${hours} uur ${minutes} min` : `${hours} uur`
}

// Lowest price in a location's pricing data, for members or non-members.
// Returns {} when there is none (same shape the overview has always used).
export const lowestPrice = (pricing, member) =>
  pricing?.data?.reduce(
    (res, item) =>
      !!item.member === member && (!res.amount || item.price < res.amount)
        ? { amount: item.price, duration: item.duration }
        : res,
    {}
  ) || {}
